import {Offer} from "../../data/models/offer.model";
import {isDigital} from "./offer-utils";

/** Zahlart-Typ der Lastschrift, wie ihn die PSA an einer Zahlart des Angebots liefert. */
export const DIRECT_DEBIT_PAYMENT_TYPE = 'DIRECTDEBITIBAN';

/** Blendet den Adress-Step in der Bestellstrecke digitaler Abos ein. */
export const CONFIG_DIGITAL_ADDRESS_STEP = 'digitalOrder.addressStep.activate';

/** Lässt die Prämienadresse digitaler Abos unter dem Titel der Lieferadresse erscheinen. */
export const CONFIG_BONUS_ADDRESS_IS_DELIVERY_ADDRESS = 'digitalOrder.bonusAddressIsDeliveryAddress';

/** Zeigt die echte Lieferadresse digitaler Abos an - in der Bestellübersicht wie auf der Bestätigung. */
export const CONFIG_SHOW_DELIVERY_ADDRESS = 'digitalOrder.showDeliveryAddress';

export const TRANSLATION_KEY_ADDRESS_BONUS = 'address_bonus';
export const TRANSLATION_KEY_ADDRESS_DELIVERY = 'address_delivery';
export const TRANSLATION_KEY_ADDRESS_DIFF_BONUS = 'address_diffBonus';
export const TRANSLATION_KEY_ADDRESS_DIFF_DELIVERY = 'address_diffDelivery';

/**
 * Liest einen Konfigurationswert als Schalter.
 *
 * Erkennt '1'/'true' und '0'/'false'. Alles andere - und das ist der wichtige Fall - liefert den
 * übergebenen Vorgabewert: Kennt der SSR-Proxy einen Schlüssel nicht, antwortet er mit
 * `{"changeable": false}` OHNE `value` (server.ts, GET /webabo/configs/**). Sein Bestand
 * (`vHost.configsObjects`) wird beim Serverstart geladen und nur über die Artemis-Topics
 * configChanged/clearCache aufgefrischt - ein frisch per Liquibase angelegter Schlüssel ist also
 * erst nach einem Neustart des Frontend-Servers sichtbar. Ein Vergleich auf einen einzelnen String
 * würde diesen Zustand still als "Vorgabe" durchgehen lassen, je nach Schreibweise auch als das
 * Gegenteil des Gemeinten.
 */
export function configFlag(config: { value?: string } | undefined, fallback: boolean): boolean {
  switch (config?.value) {
    case '1':
    case 'true':
      return true;
    case '0':
    case 'false':
      return false;
    default:
      return fallback;
  }
}

/** Wird für das Abo eine Lastschrift angeboten? Maßgeblich ist das Angebot, nicht die getroffene Wahl. */
export function isDirectDebitOffered(offer: Offer): boolean {
  return !!offer?.offerPaymentOfferType?.some(formOfPayment => formOfPayment?.paymentType === DIRECT_DEBIT_PAYMENT_TYPE);
}

/**
 * Bekommt dieses digitale Abo einen eigenen Adress-Step?
 *
 * Nur dann, wenn alle drei Bedingungen zusammenkommen:
 * - die Konfiguration {@link CONFIG_DIGITAL_ADDRESS_STEP} ist aktiv (Default: aus),
 * - das Abo ist digital (ePaper/PaidContent) - Print hat seinen Adress-Step ohnehin,
 * - für das Abo wird Lastschrift angeboten UND eine Rechnungsadresse verlangt.
 *
 * Ist der Step aktiv, wird die Rechnungsadresse ausschließlich dort abgefragt; die Zusammenfassung
 * zeigt sie dann nur noch an (s. SummaryStepComponent.isBillingAddressEditable).
 */
export function isDigitalAddressStepActive(offer: Offer, addressStepActivated: boolean): boolean {
  return addressStepActivated
    && isDigital(offer?.offerType)
    && isDirectDebitOffered(offer)
    && !!offer?.requiredAddresses?.billingAddressRequired;
}

/**
 * Unter welchem Titel die Prämienadresse erscheint.
 *
 * Digitale Abos werden nicht zugestellt und haben deshalb keine Lieferadresse. Wer eine Sachprämie
 * dazubestellt, bekommt aber sehr wohl etwas geliefert - die Prämienadresse IST dort die einzige
 * Lieferanschrift. Mit {@link CONFIG_BONUS_ADDRESS_IS_DELIVERY_ADDRESS} kann ein Mandant sie
 * deshalb überall unter dem Titel der Lieferadresse führen.
 */
export function bonusAddressTranslationKey(
  offerType: { ePaper: boolean, paidContent: boolean } | undefined,
  bonusAddressIsDeliveryAddress: boolean
): string {
  return bonusAddressIsDeliveryAddress && isDigital(offerType)
    ? TRANSLATION_KEY_ADDRESS_DELIVERY
    : TRANSLATION_KEY_ADDRESS_BONUS;
}

/**
 * Soll die echte Lieferadresse angezeigt werden - in der Bestellübersicht und auf der
 * Bestellbestätigung?
 *
 * Bei digitalen Abos steht dort die Rechnungsadresse, zugestellt wird nichts. Führt ein Mandant
 * zugleich die Prämienadresse als Lieferadresse ({@link CONFIG_BONUS_ADDRESS_IS_DELIVERY_ADDRESS}),
 * stünden zwei Blöcke gleichen Namens untereinander. Mit {@link CONFIG_SHOW_DELIVERY_ADDRESS} lässt
 * sich der überflüssige abschalten.
 *
 * Print bleibt unberührt: Dort ist die Lieferadresse die Anschrift, an die wirklich zugestellt wird.
 */
export function showDeliveryAddressBlock(
  offerType: { ePaper: boolean, paidContent: boolean } | undefined,
  showDeliveryAddressConfigured: boolean
): boolean {
  return showDeliveryAddressConfigured || !isDigital(offerType);
}

/**
 * Die Beschriftung der Checkbox, mit der eine abweichende Prämienadresse aufgeklappt wird - nach
 * derselben Regel wie {@link bonusAddressTranslationKey}. Heißt die Adresse "Lieferadresse", muss
 * die Checkbox daneben "Abweichende Lieferadresse" heißen und nicht "Abweichende Prämienadresse".
 */
export function bonusDifferentAddressTranslationKey(
  offerType: { ePaper: boolean, paidContent: boolean } | undefined,
  bonusAddressIsDeliveryAddress: boolean
): string {
  return bonusAddressIsDeliveryAddress && isDigital(offerType)
    ? TRANSLATION_KEY_ADDRESS_DIFF_DELIVERY
    : TRANSLATION_KEY_ADDRESS_DIFF_BONUS;
}
